import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import { ResponsiveContainer, ResponsiveContent, ResponsiveChatButton } from '../ui/ResponsiveLayout';
import KBHelpBox from '../ui/KBHelpBox';
import {
  DirectivesIcon,
  RecordsIcon,
  EstateAdminIcon,
  FuneralIcon,
  BelongingsIcon,
  LegacyIcon,
  PeopleIcon,
  AssetsIcon,
  DocumentsIcon,
  AccountsIcon,
  VendorsIcon
} from '../ui/Icons';
import { unifiedContactService } from '../../services/unifiedContactService';
import { setActiveFlowId } from '../../utils/activeFlow';
import { apiService } from '../../utils/api';
import { frameBelongsToFlow, getEntryFrame, getSummaryFrame, resolveResumeFrame, type FlowId } from '../../utils/frameFlowMap';
import { isFlowEffectivelyComplete } from '../../utils/flowCompletionState';


// CheckCircle (filled) for completion status
const CheckCircle = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 20 20" fill="currentColor">
    <circle cx="10" cy="10" r="10" className="fill-green-700"/>
    <path d="M7.5 10.5l2 2 3-4" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
  </svg>
);

const DIRECTIVES = [
  { label: 'Estate Admin', to: '/frame-174', icon: EstateAdminIcon },
  { label: 'Funeral Arrangements', to: '/frame-219', icon: FuneralIcon },
  { label: 'Belongings & Assets', to: '/frame-800', icon: BelongingsIcon },
  { label: 'Legacy', to: '/frame-185', icon: LegacyIcon },
];

const RECORDS = [
  { label: 'People', to: '/frame-158', completed: true, icon: PeopleIcon },
  { label: 'Assets', to: '/frame-190', completed: true, icon: AssetsIcon },
  { label: 'Documents', to: '/frame-172', completed: true, icon: DocumentsIcon },
  { label: 'Accounts', to: '/frame-801', completed: true, icon: AccountsIcon },
  { label: 'Vendors', to: '/frame-182', completed: true, icon: VendorsIcon },
];

interface FlowSummary {
  id: FlowId;
  title: string;
  icon: React.FC<{ className?: string }>;
  isCompleted: boolean;
  details: string[];
}

type LegalDocRow196 = { type?: string };
type ExecutorPick196 = { firstName?: string; lastName?: string };
type MemoryCollectionRow196 = { memories?: string; files?: unknown[] };
type PropertyRow196 = { currentValue?: number; mortgageBalance?: number };
type FinancialAssetRow196 = { value?: number };
type PersonalItemRow196 = { category?: string; estimatedValue?: number };
type DebtRow196 = { amount?: number };
type ExpenseRow196 = { frequency?: string; amount?: number };

const Frame196: React.FC = () => {
  const navigate = useNavigate();
  const { session } = useSessionPersistence();
  const [flowSummaries, setFlowSummaries] = useState<FlowSummary[]>([]);
  const [expandedFlows, setExpandedFlows] = useState<Set<string>>(new Set());
  const isCompleteFlow = (flowId: FlowId) => isFlowEffectivelyComplete({ flowId, session });


  useEffect(() => {
    const buildFlowSummaries = async () => {
      const summaries: FlowSummary[] = [];

      // Helper function to load data with localStorage fallback
      const loadWithFallback = <T,>(key: string, defaultValue: T): T => {
        try {
          const backup = localStorage.getItem(key);
          if (backup) {
            return JSON.parse(backup) as T;
          }
        } catch (error) {
          console.error(`[Frame196] Failed to load ${key}:`, error);
        }
        return defaultValue;
      };

      // 1. Legal Essentials Summary
      const selectedExecutors =
        (session.taskProgress?.executors?.answers?.selectedExecutors as ExecutorPick196[] | undefined) ?? [];

      const legalDocs =
        (session.taskProgress?.legal_documents?.answers?.documents as LegalDocRow196[] | undefined) ?? [];

      // keep this (your block), just make it slightly safer:
      const legalDocTypes = legalDocs.reduce((acc: Record<string, number>, doc: LegalDocRow196) => {
        const t = doc?.type ?? 'other';
        acc[t] = (acc[t] || 0) + 1;
        return acc;
      }, {});

      const legalDocSummary = Object.entries(legalDocTypes).map(([type, count]) => {
        const typeLabels: Record<string, string> = {
          will: 'Will',
          trust: 'Living Trust',
          healthcare_power_of_attorney: 'Healthcare POA',
          healthcare_directive: 'Healthcare Directive',
          other: 'Other',
        };
        return `${Number(count)} ${typeLabels[type] || type}`;
      });

      summaries.push({
        id: 'legal_essentials',
        title: 'Legal Essentials',
        icon: DocumentsIcon,
        isCompleted: isCompleteFlow('legal_essentials'),
        details: [
          selectedExecutors.length > 0
            ? `Executors: ${selectedExecutors.map((e) => `${e.firstName ?? ''} ${e.lastName ?? ''}`).join(', ')}`
            : 'No executors selected',
          legalDocs.length > 0
            ? `Documents: ${legalDocSummary.join(', ')}`
            : 'No legal documents uploaded',
        ],
      });

      // 2. Setup Your Plan (About) Summary
      const aboutData = session.taskProgress?.about?.answers || {};
      let contactsCount = 0;
      try {
        const contacts = await unifiedContactService.getAllContacts();
        contactsCount = contacts.length;
      } catch (error) {
        console.error('[Frame196] Error loading contacts:', error);
      }

      summaries.push({
        id: 'about',
        title: 'Setup Your Plan',
        icon: PeopleIcon,
        isCompleted: isCompleteFlow('about'),
        details: [
          `Name: ${session.name || 'Not provided'}`,
          `Email: ${session.email || 'Not provided'}`,
          `Phone: ${aboutData.phone || session.phone || 'Not provided'}`,
          `Contacts imported: ${contactsCount}`
        ]
      });

      // 3. Legacy/Memories Summary
      const memoryCollections = (session.taskProgress?.memoryCollections?.answers?.memoryCollections as MemoryCollectionRow196[] | undefined) || [];
      const peopleWithMemories = memoryCollections.filter((c) =>
        (c.memories && c.memories.trim().length > 0) ||
        (c.files && Array.isArray(c.files) && c.files.length > 0)
      );
      const totalFiles = memoryCollections.reduce((sum: number, c: MemoryCollectionRow196) => sum + ((Array.isArray(c.files) ? c.files.length : 0) || 0), 0);

      summaries.push({
        id: 'legacy',
        title: 'Legacy & Memories',
        icon: LegacyIcon,
        isCompleted: isCompleteFlow('legacy'), // Add read from session.taskProgress.memoryCollections
        details: [
          `${peopleWithMemories.length} people with memories`,
          `${totalFiles} files uploaded`,
          peopleWithMemories.length > 0 ? 'Memory collections created' : 'No memories added yet'
        ]
      });

      // 4. Inventory of Assets Summary
      const properties = (session.taskProgress?.properties?.answers?.properties as PropertyRow196[] | undefined) ||
                        loadWithFallback<PropertyRow196[]>('properties-backup', []);
      const otherFinancialAssets = (session.taskProgress?.financial_assets?.answers?.otherFinancialAssets as FinancialAssetRow196[] | undefined) ||
                                   loadWithFallback<FinancialAssetRow196[]>('other-financial-assets-backup', []);
      const taxDocs = session.taskProgress?.tax_documents?.answers?.documents ||
                     loadWithFallback<unknown[]>('tax-documents-backup', []);

      // Calculate property equity using correct field names (currentValue and mortgageBalance)
      const totalPropertyValue = properties.reduce((sum: number, prop: PropertyRow196) => sum + (Number(prop.currentValue) || 0), 0);
      const totalMortgageBalance = properties.reduce((sum: number, prop: PropertyRow196) => sum + (Number(prop.mortgageBalance) || 0), 0);
      const totalPropertyEquity = totalPropertyValue - totalMortgageBalance;

      const totalFinancialValue = otherFinancialAssets.reduce((sum: number, asset: FinancialAssetRow196) => sum + (Number(asset.value) || 0), 0);

      summaries.push({
        id: 'inventory',
        title: 'Inventory of Assets',
        icon: AssetsIcon,
        isCompleted: isCompleteFlow('inventory'), // add read from session.taskProgress.properties
        details: [
          `${properties.length} ${properties.length === 1 ? 'property' : 'properties'} - ${totalPropertyEquity.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })} equity`,
          `${otherFinancialAssets.length} financial assets - ${totalFinancialValue.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })}`,
          `${taxDocs.length} tax documents`
        ]
      });

      // 5. Funeral Planning Summary
      const funeralPrefs = session.taskProgress?.funeral?.answers?.funeralPreferences ??
                          loadWithFallback<Record<string, unknown> | null>('funeral-preferences-backup', null);
      const memorialDetails = session.taskProgress?.funeral?.answers?.memorialDetails ??
                             loadWithFallback<Record<string, unknown> | null>('memorial-details-backup', null);

      const serviceTypeLabels: Record<string, string> = {
        traditional: 'Traditional Funeral',
        memorial: 'Memorial Service',
        celebration: 'Celebration of Life',
        none: 'No Service'
      };
      const burialTypeLabels: Record<string, string> = {
        burial: 'Burial',
        cremation: 'Cremation',
        donation: 'Body Donation'
      };

      summaries.push({
        id: 'funeral',
        title: 'Funeral Planning',
        icon: FuneralIcon,
        isCompleted: isCompleteFlow('funeral'),
        details: [
          funeralPrefs && typeof funeralPrefs === 'object' && 'serviceType' in funeralPrefs && funeralPrefs.serviceType
            ? `Service: ${serviceTypeLabels[String(funeralPrefs.serviceType)]}`
            : 'Service type not selected',
          funeralPrefs && typeof funeralPrefs === 'object' && 'burialType' in funeralPrefs && funeralPrefs.burialType
            ? `Preference: ${burialTypeLabels[String(funeralPrefs.burialType)]}`
            : 'Burial preference not selected',
          memorialDetails && typeof memorialDetails === 'object' && memorialDetails.reception ? 'Reception planned' : 'No reception planned'
        ]
      });

      // 6. Personal Belongings Summary
      const personalItems = (session.taskProgress?.personal?.answers?.personalItems as PersonalItemRow196[] | undefined) ||
                           loadWithFallback<PersonalItemRow196[]>('personal-items-backup', []);
      const totalValue = personalItems.reduce((sum: number, item: PersonalItemRow196) => sum + (Number(item.estimatedValue) || 0), 0);
      const categoryBreakdown = personalItems.reduce<Record<string, number>>((acc, item) => {
        const cat = item.category || 'custom';
        acc[cat] = (acc[cat] || 0) + 1;
        return acc;
      }, {});

      const categoryLabels: Record<string, string> = {
        documents: 'Documents',
        physical_assets: 'Physical Assets',
        collectibles: 'Collectibles',
        electronics: 'Electronics',
        digital_assets: 'Digital Assets',
        custom: 'Other'
      };

      summaries.push({
        id: 'personal',
        title: 'Personal Belongings',
        icon: BelongingsIcon,
        isCompleted: isCompleteFlow('personal'),
        details: [
          `${personalItems.length} items - ${totalValue.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })}`,
          ...Object.entries(categoryBreakdown).map(([cat, count]) => `${count} ${categoryLabels[cat] || cat}`)
        ]
      });

      // 7. Debts & Expenses Summary (if completed)
      const debts = (session.taskProgress?.debts?.answers?.debts as DebtRow196[] | undefined) || [];
      const expenses = (session.taskProgress?.monthly_expenses?.answers?.expenses as ExpenseRow196[] | undefined) || [];
      const totalDebt = debts.reduce((sum: number, debt: DebtRow196) => sum + (Number(debt.amount) || 0), 0);

      // Convert expenses to monthly amounts based on frequency (matching Frame238 logic)
      const calculateMonthlyAmount = (expense: ExpenseRow196) => {
        const amt = Number(expense.amount) || 0;
        switch (expense.frequency) {
          case 'quarterly':
            return amt / 3;
          case 'annual':
            return amt / 12;
          default:
            return amt;
        }
      };
      const totalMonthlyExpenses = expenses.reduce((sum: number, exp: ExpenseRow196) => sum + calculateMonthlyAmount(exp), 0);

      if (debts.length > 0 || expenses.length > 0 || isCompleteFlow('debts')) {
        summaries.push({
          id: 'debts',
          title: 'Debts & Final Expenses',
          icon: DocumentsIcon,
          isCompleted: isCompleteFlow('debts'),
          details: [
            `${debts.length} outstanding debts - ${totalDebt.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })}`,
            `${expenses.length} monthly expenses - ${totalMonthlyExpenses.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })}/mo`
          ]
        });
      }

      // 8. Pets & Dependents Summary (if completed)
      const petsData = (session.taskProgress?.pets?.answers?.petsAndDependants as { pets?: unknown[]; dependants?: unknown[] } | undefined) ||
                      loadWithFallback<{ pets?: unknown[]; dependants?: unknown[] } | null>('pets-dependants-backup', null);
      const petsCount = petsData?.pets?.length || 0;
      const dependentsCount = petsData?.dependants?.length || 0;  // Fixed: 'dependants' not 'dependents'

      if (petsCount > 0 || dependentsCount > 0 || (isCompleteFlow('pets'))) {
        summaries.push({
          id: 'pets',
          title: 'Pets & Dependents',
          icon: PeopleIcon,
          isCompleted: isCompleteFlow('pets'),
          details: [
            `${petsCount} ${petsCount === 1 ? 'pet' : 'pets'}`,
            `${dependentsCount} ${dependentsCount === 1 ? 'dependent' : 'dependents'}`,
            petsData?.careInstructions ? 'Care instructions provided' : 'No care instructions'
          ]
        });
      }

      setFlowSummaries(summaries);
    };

    buildFlowSummaries();
  }, [session]);

  const toggleFlowExpansion = (flowId: string) => {
    setExpandedFlows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(flowId)) {
        newSet.delete(flowId);
      } else {
        newSet.add(flowId);
      }
      return newSet;
    });
  };



  const openFlowWork = async (flowId: FlowId) => {
    setActiveFlowId(flowId);

    try {
      const res = await apiService.request(`/api/flows/${flowId}/progress`, { method: 'GET' });
      const last = res?.lastFrame;

      if (typeof last === 'number' && Number.isInteger(last) && frameBelongsToFlow(last, flowId)) {
        const resumeFrame = resolveResumeFrame(flowId, last);
        navigate(`/frame-${resumeFrame}`);
        return;
      }
    } catch (e) {
      console.warn('[flow-resume] resume GET failed', e);
    }

    navigate(`/frame-${getEntryFrame(flowId)}`);
  };

  const openFlowSummary = (flowId: FlowId) => {
    setActiveFlowId(flowId);
    navigate(`/frame-${getSummaryFrame(flowId)}`);
  };




  // Calculate completion percentage
  const completedFlows = flowSummaries.filter(f => f.isCompleted).length;
  const totalFlows = flowSummaries.length;
  const completionPercentage = totalFlows > 0 ? Math.round((completedFlows / totalFlows) * 100) : 0;

  return (
    <ResponsiveContainer background="gradient">
      <ResponsiveContent>
        {/* Main content panel */}
        <div className="bg-stone-50 rounded-md shadow-[0px_0px_10px_4px_rgba(49,49,33,0.25)] p-6 lg:p-8 min-h-[600px] relative">

          {/* Header with progress */}
          <div className="text-center mb-8">
            {/* Progress indicator */}
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-3.5 h-3.5 bg-stone-400 rounded-full" />
              <span className="text-stone-400 text-xs sm:text-sm font-medium font-['Work_Sans']">
                {completionPercentage}% complete
              </span>
            </div>

            {/* Title */}
            <h1 className="text-stone-800 text-lg sm:text-xl lg:text-2xl font-semibold font-['Work_Sans']">
              {session.name ? `${session.name}'s Estate Plan` : "Your Estate Plan"}
            </h1>
          </div>

          {/* Layout Grid - Responsive */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

            {/* Left Sidebar - Navigation */}
            <div className="lg:col-span-1 space-y-6">

              {/* Your Plan heading */}
              <h2 className="text-cyan-900 text-lg sm:text-xl font-semibold font-['Test_Domaine_Display'] leading-relaxed mb-6">
                Your Plan
              </h2>

              {/* Directives Section */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 mb-3">
                  <DirectivesIcon className="w-4 h-4 text-cyan-900" />
                  <h3 className="text-black text-xs font-semibold font-['Work_Sans'] leading-none">
                    Directives
                  </h3>
                </div>
                <div className="space-y-2">
                  {DIRECTIVES.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 text-stone-800 text-sm font-medium font-['Work_Sans'] leading-none"
                    >
                      <item.icon className="w-4 h-4 text-stone-600 flex-shrink-0" />
                      <span>{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Records Section */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 mb-3">
                  <RecordsIcon className="w-4 h-4 text-cyan-900" />
                  <h3 className="text-black text-xs font-semibold font-['Work_Sans'] leading-none">
                    Records
                  </h3>
                </div>
                <div className="space-y-2">
                  {RECORDS.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 text-stone-800 text-sm font-medium font-['Work_Sans'] leading-none"
                    >
                      <item.icon className="w-4 h-4 text-stone-600 flex-shrink-0" />
                      <span>{item.label}</span>
                      {item.completed && <div className="w-3 h-3 bg-green-700 rounded-sm flex-shrink-0 ml-auto" />}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Main Summary Content */}
            <div className="lg:col-span-2">

              {/* Summary Label */}
              <h3 className="text-black text-xs font-semibold font-['Work_Sans'] leading-none mb-4">
                Overall Summary
              </h3>

              {/* Flow Summary Cards */}
              <div className="space-y-4 mb-6">
                {flowSummaries.map((flow) => (
                  <div
                    key={flow.id}
                    className="bg-white rounded-lg border border-neutral-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                  >

                    {/* Card Header - Clickable (NO nested buttons) */}
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => { void openFlowWork(flow.id); }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          void openFlowWork(flow.id);
                        }
                      }}
                      className="w-full p-4 flex items-center gap-4 text-left hover:bg-stone-50 transition-colors cursor-pointer"
                    >
                      <flow.icon className="w-6 h-6 text-cyan-700 flex-shrink-0" />
                      <div className="flex-1">
                        <h4 className="font-semibold text-stone-800 text-base">{flow.title}</h4>
                      </div>

                      {flow.isCompleted && <CheckCircle className="w-5 h-5 flex-shrink-0" />}

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFlowExpansion(flow.id);
                        }}
                        aria-label={expandedFlows.has(flow.id) ? 'Collapse details' : 'Expand details'}
                        className="text-cyan-600 hover:text-cyan-700 flex-shrink-0"
                      >
                        <svg
                          className={`w-5 h-5 transition-transform ${expandedFlows.has(flow.id) ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </div>

                    {/* Expanded Details */}
                    {expandedFlows.has(flow.id) && (
                      <div className="px-4 pb-4 border-t border-stone-100 pt-3 bg-stone-50">
                        <ul className="space-y-2">
                          {flow.details.map((detail, idx) => (
                            <li key={idx} className="text-sm text-stone-700 flex items-start gap-2">
                              <span className="text-cyan-600 mt-1">•</span>
                              <span>{detail}</span>
                            </li>
                          ))}
                        </ul>
                        <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation(); // avoids triggering card header click if nested
                              openFlowSummary(flow.id);
                            }}
                          className="mt-3 text-sm text-cyan-600 hover:text-cyan-700 font-medium hover:underline"
                        >
                          View Full Summary →
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Start Final Review Button */}
              <div className="bg-white rounded-lg border border-neutral-200 p-6">
                <p className="text-stone-800 text-sm font-normal font-['Work_Sans'] leading-relaxed mb-4">
                  {completionPercentage === 100
                    ? "You've completed all estate planning tasks. The final step is to prepare documentation for the creation of a legally conforming will or trust."
                    : `You've completed ${completedFlows} of ${totalFlows} estate planning workflows. Continue working on your plan or start the final review when ready.`
                  }
                </p>

                <button
                  disabled
                  className="bg-gray-400 text-gray-200 text-base font-medium font-['Work_Sans'] px-6 py-3 rounded cursor-not-allowed opacity-60"
                >
                  Start Final Review →
                </button>
                <p className="text-xs text-stone-500 mt-2 italic">Coming soon</p>
              </div>
            </div>
          </div>
        </div>

        {/* KB Help Articles */}
        <div className="mt-8 w-full max-w-md">
          <KBHelpBox frameId="frame-196" />
        </div>

        <ResponsiveChatButton />
      </ResponsiveContent>
    </ResponsiveContainer>
  );
};

export default Frame196;
